/* eslint-disable tailwindcss/no-custom-classname */
import Head from "next/head";
import Link from "next/link";
import Image from "next/image";
import { Tab } from "@headlessui/react";
import { Fragment, useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import ReCAPTCHA from "react-google-recaptcha";
// templates
import Meta from "@/components/templates/Meta";
import Layout from "@/components/templates/Layout";
import { Header } from "@/components/templates/Header";
import { Footer } from "@/components/templates/Footer";
// hooks
import { useBudouX } from "@/hooks/useBudouX";
import { loadRecaptchaV3Script, executeRecaptchaV3 } from "@/utils/recaptchaUtils";
// molecules
import { Container } from "@/components/molecules/Container";
import { Inner } from "@/components/molecules/Inner";
import { Button, ButtonLink, TLink } from "@/components/molecules/Button";
import {
  TextField,
  TextAreaField,
  CheckField,
} from "@/components/molecules/Form";
// icons
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFaceSmileRelaxed } from "@fortawesome/pro-thin-svg-icons";
// Images
import redesignLabBanner from "/public/ai-redesign-lab-banner.png";

export default function RedesignLaboContactPage(props) {
  const { parse } = useBudouX();
  const router = useRouter();
  const isHeaderRelative = true;
  
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    tel: '',
    mail: '',
    subject: 'redesign_labo',
    contact_content: ''
  });
  const [errors, setErrors] = useState({});
  const [isAgreed, setIsAgreed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRecaptchaV2, setShowRecaptchaV2] = useState(false);
  const [recaptchaV2Token, setRecaptchaV2Token] = useState(null);
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  const recaptchaV2Ref = useRef(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});

    try {
      let recaptchaToken = null;
      
      if (showRecaptchaV2) {
        if (!recaptchaV2Token) {
          setErrors({ general: 'reCAPTCHAの認証を完了してください。' });
          setIsSubmitting(false);
          return;
        }
        recaptchaToken = recaptchaV2Token;
      } else {
        if (!recaptchaLoaded) {
          setErrors({ general: 'セキュリティ認証の準備中です。しばらくお待ちください。' });
          setIsSubmitting(false);
          return;
        }
        
        try {
          recaptchaToken = await executeRecaptchaV3(process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY, 'contact_form');
        } catch (error) {
          console.error('reCAPTCHA v3 execution failed:', error);
          setErrors({ general: 'セキュリティ認証に失敗しました。ページを再読み込みしてお試しください。' });
          setIsSubmitting(false);
          return;
        }
      }

      const response = await fetch('/api/contact/validate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          recaptchaToken,
          recaptchaType: showRecaptchaV2 ? 'v2' : 'v3'
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.errors) {
          setErrors(data.errors);
        }
        if (data.needsV2Fallback && !showRecaptchaV2) {
          setShowRecaptchaV2(true);
          setErrors({ general: '追加のセキュリティ認証が必要です。下記のチェックボックスにチェックを入れてください。' });
        }
      } else {
        sessionStorage.setItem('contactFormData', JSON.stringify(formData));
        sessionStorage.setItem('contactFormToken', data.token);
        sessionStorage.setItem('contactFormRecaptchaToken', recaptchaToken);
        sessionStorage.setItem('contactFormRecaptchaType', showRecaptchaV2 ? 'v2' : 'v3');
        router.push('/contact/redesign_labo/confirm');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setErrors({ general: 'フォームの送信中にエラーが発生しました。' });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const savedFormData = sessionStorage.getItem('contactFormData');
    if (savedFormData) {
      try {
        const parsedData = JSON.parse(savedFormData);
        if (parsedData.subject === 'redesign_labo') {
          setFormData(parsedData);
          const savedAgreement = sessionStorage.getItem('contactFormAgreement');
          if (savedAgreement === 'true') {
            setIsAgreed(true);
          }
        }
      } catch (error) {
        console.error('Failed to restore form data:', error);
      }
    }

    const initRecaptcha = async () => {
      try {
        await loadRecaptchaV3Script(process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY);
        setRecaptchaLoaded(true);
      } catch (error) {
        console.error('Failed to load reCAPTCHA:', error);
        setErrors({ general: 'セキュリティ認証の読み込みに失敗しました。ページを再読み込みしてお試しください。' });
      }
    };

    initRecaptcha();
  }, []);

  useEffect(() => {
    const checkbox = document.querySelector('input[name="confirmTerm_check"]');
    const button = document.querySelector('.contact-form-button');
    
    if (checkbox && button) {
      const updateButtonColor = () => {
        if (isAgreed) {
          button.style.backgroundColor = '#0073bf';
          button.style.borderColor = '#0073bf';
        } else {
          button.style.backgroundColor = '';
          button.style.borderColor = '';
        }
      };
      
      checkbox.addEventListener('change', updateButtonColor);
      updateButtonColor();
      
      return () => checkbox.removeEventListener('change', updateButtonColor);
    }
  }, [isAgreed]);

  useEffect(() => {
    const initButtonColorSync = () => {
      const checkbox = document.querySelector('input[name="confirmTerm_check"]');
      const button = document.querySelector('.contact-form-button');
      
      if (checkbox && button) {
        const syncButtonColor = () => {
          if (checkbox.checked) {
            button.style.backgroundColor = '#0073bf';
            button.style.borderColor = '#0073bf';
          } else {
            button.style.backgroundColor = '';
            button.style.borderColor = '';
          }
        };
        
        syncButtonColor();
        
        const observer = new MutationObserver(() => {
          syncButtonColor();
        });
        
        observer.observe(checkbox, { attributes: true, attributeFilter: ['checked'] });
        
        checkbox.addEventListener('change', syncButtonColor);
        
        return () => {
          observer.disconnect();
          checkbox.removeEventListener('change', syncButtonColor);
        };
      }
    };
    
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initButtonColorSync);
    } else {
      initButtonColorSync();
    }
  }, []);

  useEffect(() => {
    const script = document.createElement('script');
    script.textContent = `
      (function() {
        function initContactFormButtonSync() {
          const checkbox = document.querySelector('input[name="confirmTerm_check"]');
          const button = document.querySelector('.contact-form-button');
          
          if (checkbox && button) {
            function updateButtonColor() {
              if (checkbox.checked) {
                button.style.backgroundColor = '#0073bf';
                button.style.borderColor = '#0073bf';
              } else {
                button.style.backgroundColor = '';
                button.style.borderColor = '';
              }
            }
            
            updateButtonColor();
            
            checkbox.addEventListener('change', updateButtonColor);
            
            const observer = new MutationObserver(updateButtonColor);
            observer.observe(checkbox, { attributes: true, attributeFilter: ['checked'] });
            
            setInterval(updateButtonColor, 100);
          }
        }
        
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', initContactFormButtonSync);
        } else {
          initContactFormButtonSync();
        }
        
        setTimeout(initContactFormButtonSync, 0);
        setTimeout(initContactFormButtonSync, 100);
        setTimeout(initContactFormButtonSync, 500);
      })();
    `;
    document.head.appendChild(script);
    
    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);
  return (
    <>
      <Meta
        pageTitle={"AI共創型 システムリデザインラボ 申込フォーム"}
        pageDescription={"トッカシステムズの【AI共創型 システムリデザインラボ】申込フォーム"}
        pageImg={"https://tocca.systems/ai-redesign-lab-banner.png"}
        pagePath={"https://tocca.systems/contact/redesign_labo/form/"}
        noIndex={true}
      />
      <Layout>
        <Header isRelative={isHeaderRelative} />
        <main className="flex-1">
          <Container>
            <Inner className="relative flex items-center justify-between gap-10 pt-10 pb-8 text-pri px-6 sm:px-8 md:px-12 md:py-10 lg:py-14 xl:px-0">
              <div className="space-y-3">
                <h2 className="text-2xl font-bold text-pri md:text-3xl">
                  {parse("AI共創型 システムリデザインラボ")}
                </h2>
                <div className="text-base font-bold text-ter md:text-md">
                  {parse(
                    "申込フォーム"
                  )}
                </div>
              </div>
              <h4 className="hidden text-sec md:block md:text-6xl lg:text-7xl">
                <FontAwesomeIcon icon={faFaceSmileRelaxed} />
              </h4>
            </Inner>
            <div className="relative aspect-[16/9] w-full md:aspect-[21/9]">
              <Image
                src={redesignLabBanner}
                alt="AI共創型システムリデザイン・ラボ"
                quality={90}
                layout="fill"
                objectFit="cover"
              />
            </div>
            <Inner className="grid grid-cols-1 gap-6 pt-8 pb-12 px-6 sm:px-8 md:px-12 md:grid-cols-4 md:gap-10 md:pt-14 md:pb-20 xl:px-0">
              <div className="order-2 col-span-1 md:order-1  md:col-span-3">
                <form onSubmit={handleSubmit} className="space-y-3 md:space-y-6">
                  <input type="hidden" name="subject" value="redesign_labo" />
                  <TextField
                    label="お名前"
                    id="name"
                    name="name"
                    type="text"
                    placeHolder=""
                    autoComplete="given-name"
                    value={formData.name}
                    onChange={handleInputChange}
                    error={errors.name}
                    required
                  />
                  <TextField
                    label="会社名"
                    id="company"
                    name="company"
                    type="text"
                    placeHolder=""
                    autoComplete="given-company"
                    value={formData.company}
                    onChange={handleInputChange}
                    error={errors.company}
                    required
                  />
                  <TextField
                    label="電話番号"
                    id="tel"
                    name="tel"
                    type="text"
                    placeHolder="日中ご連絡の付きやすい番号を入力してください"
                    autoComplete="given-tel"
                    value={formData.tel}
                    onChange={handleInputChange}
                    error={errors.tel}
                    required
                  />
                  <TextField
                    label="メールアドレス"
                    id="mail"
                    name="mail"
                    type="email"
                    placeHolder=""
                    autoComplete="given-mail"
                    value={formData.mail}
                    onChange={handleInputChange}
                    error={errors.mail}
                    required
                  />
                  <TextAreaField
                    label="相談したい内容"
                    id="contact_content"
                    name="contact_content"
                    placeHolder="ご相談内容やプロジェクトの概要などをご記入ください。"
                    value={formData.contact_content}
                    onChange={handleInputChange}
                    error={errors.contact_content}
                    required
                  />
                  <div className="space-y-2">
                    <p className="text-sm text-txt-light">
                      ※ご入力いただいた情報は弊社、
                      <TLink href="/policy" variant="mono" color="pp">
                        個人情報保護方針
                      </TLink>
                      に則り管理いたします
                    </p>
                    <CheckField
                      className="gap-3"
                      label="個人情報保護方針に同意する"
                      id="confirmTerm_check"
                      name="confirmTerm_check"
                      checked={isAgreed}
                      onChange={(e) => {
                        setIsAgreed(e.target.checked);
                        sessionStorage.setItem('contactFormAgreement', e.target.checked.toString());
                      }}
                    />
                  </div>
                  {showRecaptchaV2 && (
                    <div className="space-y-2">
                      <p className="text-sm text-txt-light">
                        セキュリティ認証のため、下記のチェックボックスにチェックを入れてください。
                      </p>
                      <div className="flex justify-center">
                        <ReCAPTCHA
                          ref={recaptchaV2Ref}
                          sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_V2_SITE_KEY}
                          onChange={(token) => {
                            setRecaptchaV2Token(token);
                            if (errors.general) {
                              setErrors({});
                            }
                          }}
                          onExpired={() => {
                            setRecaptchaV2Token(null);
                          }}
                          onError={() => {
                            setRecaptchaV2Token(null);
                            setErrors({ general: 'reCAPTCHA認証でエラーが発生しました。ページを再読み込みしてお試しください。' });
                          }}
                        />
                      </div>
                    </div>
                  )}
                  {errors.general && (
                    <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
                      {errors.general}
                    </div>
                  )}
                  <Button
                    type="submit"
                    variant="solid"
                    color="ter"
                    className={`h-16 w-full rounded-md md:h-24 contact-form-button ${
                      !isAgreed || isSubmitting || (showRecaptchaV2 && !recaptchaV2Token)
                        ? 'opacity-50 cursor-not-allowed' 
                        : ''
                    }`}
                    disabled={!isAgreed || isSubmitting || (showRecaptchaV2 && !recaptchaV2Token)}
                  >
                    {isSubmitting ? '送信中...' : '送信内容を確認する'}
                  </Button>
                  <style jsx>{`
                    form:has(input[name="confirmTerm_check"]:checked) .contact-form-button {
                      background-color: #0073bf !important;
                      border-color: #0073bf !important;
                    }
                  `}</style>
                </form>
              </div>
              <div className="order-1 col-span-1 md:order-2">
                <h3 className="mb-2 text-md font-bold text-pri md:text-base">
                  Application Form
                </h3>
                <div className="text-base text-txt-base md:text-sm">
                  {parse("既存システムの課題を「見える化」し、優先順位を付けて段階的に改善します。AIと専門チームが提案から実装、運用まで一気通貫で伴走し、お客様のシステムを持続的に進化させます。")}
                </div>
                <div className="mt-4 text-base text-txt-base md:text-sm">
                  {parse("まずは無料ミニ診断（1〜2週間）から始められます。お気軽にお申し込みください。")}
                </div>
              </div>
            </Inner>
          </Container>
        </main>
        <Footer />
      </Layout>
    </>
  );
}
